import type {
  FocusEvent,
  HTMLAttributes,
  KeyboardEvent,
  MutableRefObject,
  Ref,
  RefObject,
} from 'react'
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

import { padding, tint } from 'polished'
import ReactSelect, {
  type AriaOnChangeProps,
  type ClearIndicatorProps,
  type ControlProps,
  components as ReactSelectComponents,
  type DropdownIndicatorProps,
  type GroupBase,
  type InputActionMeta,
  type InputProps,
  type MenuListProps,
  type SelectInstance,
  type StylesConfig,
  type ThemeConfig,
  useStateManager,
} from 'react-select'
import { type CreatableProps, useCreatable } from 'react-select/creatable'
import { type DefaultTheme, useTheme } from 'styled-components'
import styled from 'styled-components'

import { IconChevronDown, IconChevronUp, IconX } from '../Icon'
import { ScrollCapture } from './utils/ScrollCapture'
import { getAnalyticsDataAttrs } from '@ui/utils/analytics'

declare module 'react-select/dist/declarations/src/Select' {
  export interface Props<
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,
  > {
    stickies?: (props: any) => JSX.Element[]
    analyticsId?: string
    variant?: 'default' | 'borderless'
    /**
     * Announces the select as required on its input. Unlike react-select's
     * `required`, it renders no hidden native required input, so it never
     * triggers the browser's own form validation. The `Form` select wrappers
     * set it from their `required` prop.
     */
    'aria-required'?: boolean
    /**
     * Ids of elements that describe the select, such as a hint beside it.
     * They are added to the input's `aria-describedby` after react-select's
     * own ids (its placeholder, or its live region on focus), which it would
     * otherwise overwrite, so those descriptions still work. Applies to the
     * searchable input (the default), as `aria-required` does.
     */
    'aria-describedby'?: string
  }
}

export interface SelectValue<T = any> {
  label: string
  value: T
}

export interface CreatableSelectValue extends SelectValue {
  __isNew__: boolean
}

const Control = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ControlProps<Option, IsMulti, Group>
) => {
  const { analyticsId } = props.selectProps
  const innerProps = props.innerProps as HTMLAttributes<HTMLDivElement>
  const analyticsProps = getAnalyticsDataAttrs(analyticsId)

  return (
    <ReactSelectComponents.Control
      {...props}
      innerProps={{
        ...innerProps,
        ...analyticsProps,
      }}
    >
      {props.children}
    </ReactSelectComponents.Control>
  )
}

const Input = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: InputProps<Option, IsMulti, Group>
) => {
  const describedBy = [
    props['aria-describedby'],
    props.selectProps['aria-describedby'],
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <ReactSelectComponents.Input
      {...props}
      aria-required={
        props['aria-required'] || props.selectProps['aria-required']
      }
      aria-describedby={describedBy || undefined}
    />
  )
}

const IndicatorsContainer = (props) => {
  const { children, ...otherProps } = props

  const newChildren = children.slice()

  const [clearIndicator, LoadingIndicator, , dropdownIndicator] = newChildren

  return (
    <ReactSelectComponents.IndicatorsContainer {...otherProps}>
      {LoadingIndicator}
      {clearIndicator ? clearIndicator : dropdownIndicator}
    </ReactSelectComponents.IndicatorsContainer>
  )
}

const DropdownIndicator = (props: DropdownIndicatorProps<any>) => {
  return (
    <ReactSelectComponents.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen ? (
        <IconChevronUp size={12} />
      ) : (
        <IconChevronDown size={12} />
      )}
    </ReactSelectComponents.DropdownIndicator>
  )
}

const ClearIndicator = (props: ClearIndicatorProps<any>) => {
  const activeTheme = useTheme()

  return (
    <ReactSelectComponents.ClearIndicator {...props}>
      <IconX
        size={10}
        style={{
          color: activeTheme.colors.textPrimary,
          fontWeight: activeTheme.fontWeights.semibold,
        }}
      />
    </ReactSelectComponents.ClearIndicator>
  )
}

const StickyOptionStyled = styled.div`
  font-size: ${(props) => props.theme.typography.pxToRem(16)};

  && {
    ${(props) => padding(props.theme.spacing())};
    font-size: ${(props) => props.theme.typography.pxToRem(12)};
    cursor: pointer;

    & + & {
      border-top: 1px solid ${(props) => props.theme.colors.content.border};
    }

    &:hover,
    &:focus-within {
      background-color: ${(props) => tint(0.75, props.theme.colors.darkGrey)};
    }

    /* A row that is one action hands the action its padding, so the whole
       highlighted row is the click target. */
    &:has(> button:only-child),
    &:has(> a[href]:only-child) {
      padding: 0;
    }

    > button:only-child,
    > a[href]:only-child {
      display: block;
      width: 100%;
      ${(props) => padding(props.theme.spacing())};
      text-align: start;
    }

    > button:only-child:focus-visible,
    > a[href]:only-child:focus-visible {
      outline: ${(props) =>
        `${props.theme.typography.pxToRem(1)} solid ${props.theme.colors.focus}`};
    }
  }
`

const StickyContainerStyled = styled.div`
  font-size: ${(props) => props.theme.typography.pxToRem(16)};
  border-top: 1px solid ${(props) => props.theme.colors.content.border};
`

/**
 * react-select 5.8.0's default `ariaLiveMessages.onChange` (react-select does
 * not export its defaults), except that a message with no option label stays
 * empty: react-select says "option , selected." whenever an empty select takes
 * focus.
 */
const announceChange = <Option, IsMulti extends boolean>({
  action,
  label = '',
  labels,
  isDisabled,
}: AriaOnChangeProps<Option, IsMulti>): string => {
  switch (action) {
    case 'deselect-option':
    case 'pop-value':
    case 'remove-value':
      return `option ${label}, deselected.`
    case 'clear':
      return 'All selected options have been cleared.'
    case 'initial-input-focus':
      if (!labels.length) return ''
      return `option${labels.length > 1 ? 's' : ''} ${labels.join(',')}, selected.`
    case 'select-option':
      if (!label) return ''
      return isDisabled
        ? `option ${label} is disabled. Select another option.`
        : `option ${label}, selected.`
    default:
      return ''
  }
}

// The creatable hook runs on every select, so a select that is not creatable
// must not evaluate a create option: react-select's default check passes every
// entry of `options`, groups included, to `getOptionValue`/`getOptionLabel`,
// which throw on a group when written for flat options.
const isNeverValidNewOption = () => false

interface ISelectStickiesContext {
  containerRef: RefObject<HTMLDivElement>
  focusInput: () => void
  isInput: (node: Node) => boolean
}

const SelectStickiesContext =
  createContext<Nullable<ISelectStickiesContext>>(null)

// The interactive content `Select.StickyOption` allows: real links and buttons.
const STICKY_ACTION_SELECTOR = 'a[href], button:not([disabled])'

const getStickyActions = (container: Nullable<HTMLElement>): HTMLElement[] =>
  container
    ? Array.from(
        container.querySelectorAll<HTMLElement>(STICKY_ACTION_SELECTOR)
      )
    : []

const MenuList = (props: MenuListProps<any>) => {
  const { selectProps } = props
  const stickiesContext = useContext(SelectStickiesContext)

  // Closes the way react-select's own Escape does: clears the search term and
  // closes the menu, without sending a value change.
  const closeMenu = () => {
    selectProps.onInputChange('', {
      action: 'menu-close',
      prevInputValue: selectProps.inputValue,
    })
    selectProps.onMenuClose()
  }

  // Sticky actions are controls of their own: react-select's option keys
  // (Enter, Space, Tab, arrows) and a dialog's Escape must not act on them.
  // The arrows carry on the menu's navigation through the actions, and up from
  // the first action returns to the options.
  const handleStickiesKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()

    if (event.key === 'Escape') {
      stickiesContext?.focusInput()
      closeMenu()
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()

      const actions = getStickyActions(event.currentTarget)
      const index = actions.findIndex((action) =>
        action.contains(document.activeElement)
      )
      const nextAction =
        event.key === 'ArrowDown' ? actions[index + 1] : actions[index - 1]

      if (nextAction) {
        nextAction.focus()
      } else if (event.key === 'ArrowUp') {
        stickiesContext?.focusInput()
      }
    }
  }

  const handleStickiesBlur = (event: FocusEvent<HTMLDivElement>) => {
    const { relatedTarget } = event
    const isFocusStayingInSelect =
      relatedTarget instanceof Node &&
      (event.currentTarget.contains(relatedTarget) ||
        Boolean(stickiesContext?.isInput(relatedTarget)))

    if (isFocusStayingInSelect) return

    selectProps.onInputChange('', {
      action: 'input-blur',
      prevInputValue: selectProps.inputValue,
    })
    selectProps.onMenuClose()
  }

  return (
    <Fragment>
      <ScrollCapture onBottomArrive={selectProps.onMenuScrollToBottom}>
        <ReactSelectComponents.MenuList {...props}>
          {props.children}
        </ReactSelectComponents.MenuList>
      </ScrollCapture>
      {selectProps.stickies && (
        <StickyContainerStyled
          ref={stickiesContext?.containerRef}
          onKeyDown={handleStickiesKeyDown}
          onBlur={handleStickiesBlur}
        >
          {selectProps.stickies({ ...props, closeMenu })}
        </StickyContainerStyled>
      )}
    </Fragment>
  )
}

const defaultStyles = (
  activeTheme: DefaultTheme,
  error: ISelectProps['error'],
  removeValueMargin: ISelectProps['removeValueMargin']
) => ({
  option: (base, state) => {
    return {
      ...base,
      fontSize: activeTheme.typography.pxToRem(12),
      color: activeTheme.colors.textPrimary,
      backgroundColor: state.isDisabled
        ? activeTheme.colors.form.field.backgroundDisabled
        : base.backgroundColor,
    }
  },
  group: (base) => {
    return {
      ...base,
      paddingBottom: 0,
      paddingTop: activeTheme.typography.pxToRem(12),
    }
  },
  menuPortal: (base) => {
    return {
      ...base,
      zIndex: 9999,
    }
  },
  placeholder: (base) => {
    return {
      ...base,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      left: 8,
      right: 0,
      color: activeTheme.colors.text.placeholder,
      fontSize: activeTheme.typography.pxToRem(14),
      lineHeight: 1.2,
    }
  },
  singleValue: (base, state) => ({
    ...base,
    lineHeight: 1.2,
    fontSize: activeTheme.typography.pxToRem(14),
    marginTop: removeValueMargin ? 0 : -1,
    opacity: state.selectProps.menuIsOpen ? '0.3' : '1',
    color: state.isDisabled
      ? activeTheme.colors.form.field.textDisabled
      : activeTheme.colors.textPrimary,
  }),
  input: (base, state) => {
    return {
      ...base,
      fontSize: activeTheme.typography.pxToRem(14),
      input: {
        height: 'auto',
      },
    }
  },
  control: (base, state) => {
    return {
      ...base,
      boxShadow: 'none',
      minWidth: 100,
      backgroundColor: state.isDisabled
        ? activeTheme.colors.form.field.backgroundDisabled
        : activeTheme.colors.white,
      borderColor: state.isDisabled
        ? activeTheme.colors.form.field.borderDisabled
        : error
          ? activeTheme.colors.form.field.borderError
          : state.isFocused
            ? activeTheme.colors.content.border
            : state.theme.colors.neutral20,
      '&:hover': {
        borderColor: state.isDisabled
          ? activeTheme.colors.form.field.borderDisabled
          : error
            ? activeTheme.colors.form.field.borderError
            : state.isFocused
              ? activeTheme.colors.content.border
              : state.theme.colors.neutral30,
      },
    }
  },
  dropdownIndicator: (base) => {
    return {
      ...base,
      color: activeTheme.colors.textPrimary,
      fontWeight: activeTheme.fontWeights.semibold,
    }
  },
  container: (base, state) => {
    return {
      ...base,
      fontWeight: activeTheme.fontWeights.normal,
    }
  },
})

const borderlessVariantStyles = (
  activeTheme: DefaultTheme,
  error: ISelectProps['error'],
  removeValueMargin: ISelectProps['removeValueMargin']
) => ({
  singleValue: (base, state) => ({
    ...base,
    lineHeight: 1.2,
    marginTop: removeValueMargin ? 0 : -1,
    opacity: state.selectProps.menuIsOpen ? '0.3' : '1',
    color: state.isDisabled
      ? activeTheme.colors.textDisabled
      : activeTheme.colors.textLink,
    fontFamily: activeTheme.fontFamily.header,
    fontWeight: activeTheme.fontWeights.semibold,
    fontSize: activeTheme.typography.pxToRem(18),
  }),
  placeholder: (base, state) => {
    return {
      ...base,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      left: 8,
      right: 0,
      fontFamily: activeTheme.fontFamily.header,
      fontWeight: activeTheme.fontWeights.semibold,
      fontSize: activeTheme.typography.pxToRem(18),
      color: state.isDisabled
        ? activeTheme.colors.textDisabled
        : activeTheme.colors.textLink,
      opacity: 0.3,
    }
  },
  input: (base, state) => {
    return {
      ...base,
      fontSize: activeTheme.typography.pxToRem(18),
      fontFamily: activeTheme.fontFamily.header,
      fontWeight: activeTheme.fontWeights.semibold,
      input: {
        height: 'auto',
      },
    }
  },
  valueContainer: (base, state) => {
    return {
      ...base,
      padding: '0px',
    }
  },
  control: (base, state) => {
    return {
      ...base,
      borderRadius: 0,
      boxShadow: 'none',
      maxWidth: 320,
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: state.isDisabled
        ? '2px solid ' + activeTheme.colors.disabled
        : error
          ? '2px solid ' + activeTheme.colors.form.field.borderError
          : '2px solid ' + activeTheme.colors.textLink,
      '&:hover': {
        border: 'none',
        borderBottom: state.isDisabled
          ? '2px solid ' + activeTheme.colors.disabled
          : error
            ? '2px solid ' + activeTheme.colors.form.field.borderError
            : '2px solid ' + activeTheme.colors.textLink,
        cursor: 'pointer',
      },
    }
  },
  container: (base, state) => {
    return {
      ...base,
      backgroundColor: 'transparent',
    }
  },
  dropdownIndicator: (base, state) => {
    return {
      ...base,
      color: state.isDisabled
        ? activeTheme.colors.textDisabled
        : activeTheme.colors.textLink,
    }
  },
  loadingIndicator: (base) => {
    return {
      ...base,
      display: 'none',
    }
  },
})

export const useSelectStylesAndTheme = <
  Option,
  IsMulti extends boolean = false,
>({
  error,
  removeValueMargin,
  variant,
}: {
  error: ISelectProps['error']
  removeValueMargin: ISelectProps['removeValueMargin']
  variant: ISelectProps['variant']
}): {
  styles: StylesConfig<Option, IsMulti, GroupBase<Option>>
  theme: ThemeConfig
} => {
  const activeTheme = useTheme()

  let variantStyles = {}
  switch (variant) {
    case 'borderless':
      variantStyles = borderlessVariantStyles(
        activeTheme,
        error,
        removeValueMargin
      )
      break
    case 'default':
    default:
      break
  }

  return {
    styles: {
      ...defaultStyles(activeTheme, error, removeValueMargin),
      ...variantStyles,
    },
    theme: (theme) => {
      return {
        ...theme,
        colors: {
          ...theme.colors,
          neutral20: activeTheme.colors.content.border,
          neutral30: activeTheme.colors.darkGrey,
          primary: activeTheme.colors.form.select.optionBackgroundActive,
          primary25: activeTheme.colors.form.select.optionBackgroundHover,
          primary50: activeTheme.colors.form.select.optionBackgroundHover,
          primary75: activeTheme.colors.form.select.optionBackgroundHover,
          bgDisabled: activeTheme.colors.form.field.backgroundDisabled,
          bgPrimary: activeTheme.colors.white,
        },
        fontWeights: {
          ...activeTheme.fontWeights,
        },
      }
    },
  }
}

export interface ISelectProps<
  Option = any,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends CreatableProps<Option, IsMulti, Group> {
  /*
   * The docgen propFilter in .storybook/main.js hides props typed in
   * node_modules, so we redeclare these react-select props to show them in
   * Storybook.
   */

  /**
   * Text the control shows while nothing is selected.
   * @default 'Select...'
   */
  placeholder?: CreatableProps<Option, IsMulti, Group>['placeholder']
  /**
   * Allow more than one selection. Selected options render as removable
   * tags, and `value` and `onChange` work with arrays.
   * @default false
   */
  isMulti?: CreatableProps<Option, IsMulti, Group>['isMulti']
  /**
   * Show a clear (×) button in place of the dropdown chevron once the user
   * selects a value. Clearing calls `onChange` with `null`, or `[]` when
   * `isMulti`. Defaults to `isMulti`.
   */
  isClearable?: boolean
  /**
   * Grey out the control and block interaction.
   * @default false
   */
  isDisabled?: boolean
  /**
   * Show a loading spinner in the control, except in the `borderless`
   * variant. `AsyncSelect` sets it while options load.
   * @default false
   */
  isLoading?: boolean
  /**
   * Let the user type to filter the options.
   * @default true
   */
  isSearchable?: boolean
  /**
   * Close the menu after the user picks an option. Turn it off with `isMulti`
   * so the user can pick several options without reopening the menu.
   * @default true
   */
  closeMenuOnSelect?: boolean
  /**
   * Returns the menu content for a search that matches no option. Receives
   * `{ inputValue }`, the user's search text.
   * @default () => 'No options'
   */
  noOptionsMessage?: CreatableProps<Option, IsMulti, Group>['noOptionsMessage']
  /**
   * Where the menu opens relative to the control. `auto` opens it above
   * when there is no room below.
   * @default 'bottom'
   */
  menuPlacement?: CreatableProps<Option, IsMulti, Group>['menuPlacement']

  /**
   * Renders `Select.StickyOption` actions below the options list, outside
   * its scroll area, such as "Add new tax". Receives the menu list props
   * (including `selectOption`) and `closeMenu`. `closeMenu` re-selects the
   * current value, which fires `onChange`, so call it before any state update
   * that has to win.
   */
  stickies?: (props: any) => JSX.Element[]
  /**
   * Mark the field invalid with a danger border (a red underline in the
   * `borderless` variant). Set `error` on the wrapping `Field` as well so its
   * helper text matches.
   */
  error?: boolean
  /**
   * Remove the selected value's default `margin-top: -1px`.
   */
  removeValueMargin?: boolean
  /**
   * Let the user add an option that isn't in `options` by typing it. Created
   * options carry `__isNew__: true` (see `CreatableSelectValue`). Don't
   * change it after mount: `Select` calls `useCreatable` only when it's set,
   * so toggling it changes the hook order and React throws.
   */
  creatable?: boolean
  /**
   * `borderless` swaps the box for a coloured underline and heading-size
   * value text, for a picker inside a heading.
   */
  variant?: 'default' | 'borderless'
  /**
   * Unique identifier for analytics tracking of select interactions.
   * The control renders it as `data-analytics-id`.
   */
  analyticsId?: string
  /**
   * Ref to the react-select instance, for moving focus to the select from
   * outside: `selectRef.current?.focus()`. A named prop rather than `ref`, so
   * it passes through wrappers that spread their props, `AsyncSelect` and
   * `WithFormControlSelect`.
   */
  selectRef?: Ref<SelectInstance<Option, IsMulti, Group>>
}

export const Select = <Option, IsMulti extends boolean = false>({
  className,
  components,
  error,
  removeValueMargin,
  styles = {},
  creatable,
  analyticsId,
  variant = 'default',
  selectRef,
  ...otherProps
}: ISelectProps<Option, IsMulti>) => {
  const { styles: variantStyles, theme } = useSelectStylesAndTheme<
    Option,
    IsMulti
  >({
    error,
    removeValueMargin,
    variant,
  })

  const stateProps = useStateManager(otherProps)
  const creatableProps = useCreatable({
    ...stateProps,
    isValidNewOption: creatable
      ? stateProps.isValidNewOption
      : isNeverValidNewOption,
  })
  const selectProps = creatable ? creatableProps : stateProps

  const {
    ariaLiveMessages,
    menuIsOpen,
    onBlur,
    onInputChange,
    onKeyDown,
    onMenuClose,
  } = selectProps

  const selectAriaLiveMessages = useMemo(
    () => ({ onChange: announceChange, ...ariaLiveMessages }),
    [ariaLiveMessages]
  )

  const instanceRef = useRef<Nullable<SelectInstance<Option, IsMulti>>>(null)
  const stickiesRef = useRef<HTMLDivElement>(null)
  const keepMenuOpenOnBlurRef = useRef(false)

  const stickiesContext = useMemo(
    () => ({
      containerRef: stickiesRef,
      focusInput: () => instanceRef.current?.focus(),
      isInput: (node: Node) => node === instanceRef.current?.inputRef,
    }),
    []
  )

  const setSelectRef = useCallback(
    (instance: Nullable<SelectInstance<Option, IsMulti>>) => {
      instanceRef.current = instance

      if (typeof selectRef === 'function') {
        selectRef(instance)
      } else if (selectRef) {
        ;(
          selectRef as MutableRefObject<
            Nullable<SelectInstance<Option, IsMulti>>
          >
        ).current = instance
      }
    },
    [selectRef]
  )

  // Moving focus from the input into a sticky action blurs the input, and
  // react-select would then clear the term and close the menu, unmounting the
  // action that just took focus. The blur's own clear and close are skipped;
  // react-select's blur handling ends with onMenuClose.
  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { relatedTarget } = event

    keepMenuOpenOnBlurRef.current =
      relatedTarget instanceof Node &&
      Boolean(stickiesRef.current?.contains(relatedTarget))

    onBlur?.(event)
  }

  const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    const isBlurClearing =
      actionMeta.action === 'input-blur' || actionMeta.action === 'menu-close'

    if (keepMenuOpenOnBlurRef.current && isBlurClearing) return

    onInputChange(newValue, actionMeta)
  }

  const handleMenuClose = () => {
    if (keepMenuOpenOnBlurRef.current) {
      keepMenuOpenOnBlurRef.current = false
      return
    }

    onMenuClose()
  }

  const isLastOptionFocused = () => {
    const instance = instanceRef.current
    const options = instance?.getFocusableOptions() ?? []

    return (
      !options.length ||
      instance?.state.focusedOption === options[options.length - 1]
    )
  }

  // react-select closes its menu on Escape without stopping the event, and
  // MUI Modal closes on any Escape that reaches it, so the first Escape would
  // close the whole dialog instead of just the menu. Down from the last option
  // moves on to the sticky actions instead of wrapping to the first option:
  // Tab selects the focused option, so the arrows are the path to the actions.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && menuIsOpen) {
      event.stopPropagation()
    }
    onKeyDown?.(event)

    if (
      event.key === 'ArrowDown' &&
      menuIsOpen &&
      !event.defaultPrevented &&
      isLastOptionFocused()
    ) {
      const [firstAction] = getStickyActions(stickiesRef.current)

      if (firstAction) {
        event.preventDefault()
        firstAction.focus()
      }
    }
  }

  return (
    <SelectStickiesContext.Provider value={stickiesContext}>
      <ReactSelect
        ref={setSelectRef}
        analyticsId={analyticsId}
        className={`react-select ${className}`}
        classNamePrefix='react-select'
        captureMenuScroll={false}
        components={{
          Control,
          Input,
          IndicatorsContainer,
          ClearIndicator,
          DropdownIndicator,
          MenuList,
          ...components,
        }}
        styles={{
          ...variantStyles,
          ...styles,
        }}
        theme={theme}
        {...selectProps}
        ariaLiveMessages={selectAriaLiveMessages}
        onBlur={handleBlur}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onMenuClose={handleMenuClose}
      />
    </SelectStickiesContext.Provider>
  )
}

/**
 * One row of the `stickies` area under the options. The row is a plain
 * container with no keyboard path of its own: put interactive content inside
 * it as a real `button` (with `type='button'`) or `a`, and the select makes it
 * keyboard-reachable:
 *
 * - Down from the last option moves focus to the first action; Up and Down
 *   move between actions, and Up from the first returns to the options.
 * - Tab keeps react-select's meaning, selecting the focused option, so it
 *   only reaches the actions when no option is focused.
 * - Escape returns to the input, and moving focus out of the select closes
 *   the menu.
 *
 * A row that holds such content is highlighted while it has focus, as on
 * hover. When the action is the row's only child, it fills the row, so a
 * click anywhere on the highlighted row reaches it, and it shows the focus
 * ring on keyboard focus. An `onClick` on the row itself stays pointer-only.
 */
Select.StickyOption = StickyOptionStyled
